import { TypographyStylesProvider } from "@mantine/core"
import { createElement, Fragment, useMemo } from "react"

const INLINE_PATTERNS = [
  { type: "code", regex: /`([^`]+)`/ },
  { type: "link", regex: /\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^)]+)\)/i },
  { type: "autolink", regex: /<((?:https?:\/\/|mailto:)[^>\s]+)>/i },
  { type: "strong", regex: /\*\*([^*]+)\*\*/ },
  { type: "em", regex: /\*([^*\n]+)\*/ },
  { type: "bare_url", regex: /\bhttps?:\/\/[^\s<]+[^\s<.,:;"')\]]/i },
]

function joinClassNames(...classNames) {
  return classNames.filter(Boolean).join(" ")
}

function isBlankLine(line) {
  return /^\s*$/.test(line)
}

function matchListItem(line) {
  return line.match(/^(\s*)([-+*]|\d+\.)\s+(.*)$/)
}

function sanitizeHref(rawHref) {
  if (!rawHref) return null

  const href = String(rawHref).trim()
  return /^(https?:\/\/|mailto:)/i.test(href) ? href : null
}

function pushTextNodes(nodes, text, keyBase) {
  if (!text) return

  const parts = String(text).split("\n")
  parts.forEach((part, index) => {
    if (part) nodes.push(part)
    if (index < parts.length - 1) {
      nodes.push(<br key={`${keyBase}-br-${index}`} />)
    }
  })
}

function parseInline(text, keyBase = "md-inline") {
  if (!text) return []

  const nodes = []
  let remaining = String(text)
  let tokenIndex = 0

  while (remaining.length > 0) {
    let earliest = null

    INLINE_PATTERNS.forEach((pattern, patternIndex) => {
      const match = pattern.regex.exec(remaining)
      if (!match) return

      const candidate = { pattern, patternIndex, match, index: match.index }
      if (
        !earliest ||
        candidate.index < earliest.index ||
        (candidate.index === earliest.index && candidate.patternIndex < earliest.patternIndex)
      ) {
        earliest = candidate
      }
    })

    if (!earliest) {
      pushTextNodes(nodes, remaining, `${keyBase}-${tokenIndex}`)
      break
    }

    const { pattern, match, index } = earliest
    if (index > 0) {
      pushTextNodes(nodes, remaining.slice(0, index), `${keyBase}-${tokenIndex}-text`)
    }

    const fullMatch = match[0]
    const key = `${keyBase}-${tokenIndex}`

    if (pattern.type === "code") {
      nodes.push(<code key={key}>{match[1]}</code>)
    } else if (pattern.type === "link") {
      const href = sanitizeHref(match[2])
      if (href) {
        nodes.push(
          <a key={key} href={href} target="_blank" rel="noreferrer noopener">
            {parseInline(match[1], `${key}-label`)}
          </a>,
        )
      } else {
        pushTextNodes(nodes, fullMatch, `${key}-invalid-link`)
      }
    } else if (pattern.type === "autolink" || pattern.type === "bare_url") {
      const href = sanitizeHref(match[1] || fullMatch)
      if (href) {
        nodes.push(
          <a key={key} href={href} target="_blank" rel="noreferrer noopener">
            {href}
          </a>,
        )
      } else {
        pushTextNodes(nodes, fullMatch, `${key}-invalid-url`)
      }
    } else if (pattern.type === "strong") {
      nodes.push(<strong key={key}>{parseInline(match[1], `${key}-strong`)}</strong>)
    } else if (pattern.type === "em") {
      nodes.push(<em key={key}>{parseInline(match[1], `${key}-em`)}</em>)
    } else {
      pushTextNodes(nodes, fullMatch, `${key}-fallback`)
    }

    remaining = remaining.slice(index + fullMatch.length)
    tokenIndex += 1
  }

  return nodes
}

function parseBlocks(source) {
  const normalized = String(source || "").replace(/\r\n?/g, "\n")
  const lines = normalized.split("\n")
  const blocks = []
  let index = 0

  while (index < lines.length) {
    if (isBlankLine(lines[index])) {
      index += 1
      continue
    }

    const headingMatch = lines[index].match(/^(#{1,6})\s+(.*)$/)
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2].trim(),
      })
      index += 1
      continue
    }

    const listMatch = matchListItem(lines[index])
    if (listMatch) {
      const ordered = /\d+\./.test(listMatch[2])
      const items = []

      while (index < lines.length) {
        const currentMatch = matchListItem(lines[index])
        if (!currentMatch || /\d+\./.test(currentMatch[2]) !== ordered) break

        const itemLines = [currentMatch[3].trim()]
        index += 1

        while (index < lines.length) {
          if (isBlankLine(lines[index])) break
          if (lines[index].match(/^(#{1,6})\s+/)) break
          if (matchListItem(lines[index])) break

          itemLines.push(lines[index].trim())
          index += 1
        }

        items.push(itemLines.join("\n"))
      }

      blocks.push({ type: ordered ? "ordered_list" : "unordered_list", items })
      continue
    }

    const paragraphLines = []
    while (index < lines.length) {
      if (isBlankLine(lines[index])) break
      if (lines[index].match(/^(#{1,6})\s+/)) break
      if (matchListItem(lines[index])) break

      paragraphLines.push(lines[index].trim())
      index += 1
    }

    blocks.push({ type: "paragraph", text: paragraphLines.join("\n") })
  }

  return blocks
}

function renderBlock(block, index) {
  if (block.type === "heading") {
    const HeadingTag = `h${Math.min(block.level + 1, 6)}`
    return createElement(HeadingTag, { key: `block-${index}` }, parseInline(block.text, `block-${index}`))
  }

  if (block.type === "unordered_list" || block.type === "ordered_list") {
    const ListTag = block.type === "ordered_list" ? "ol" : "ul"
    return createElement(
      ListTag,
      { key: `block-${index}` },
      block.items.map((item, itemIndex) => (
        <li key={`block-${index}-item-${itemIndex}`}>{parseInline(item, `block-${index}-item-${itemIndex}`)}</li>
      )),
    )
  }

  return <p key={`block-${index}`}>{parseInline(block.text, `block-${index}`)}</p>
}

export function MarkdownContent({ source, className = "", style }) {
  const blocks = useMemo(() => parseBlocks(source), [source])
  if (!blocks.length) return null

  return (
    <TypographyStylesProvider className={joinClassNames("af-markdown", className)} style={style}>
      {blocks.map((block, index) => (
        <Fragment key={`markdown-block-${index}`}>{renderBlock(block, index)}</Fragment>
      ))}
    </TypographyStylesProvider>
  )
}
